/**
 * Thin logger wrapper that suppresses debug/info/log traffic in production
 * but preserves warnings and errors so real issues still surface in the
 * browser console AND get forwarded to Sentry (iter132).
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.log('WhatsApp diagnostics', payload);   // dev only
 *   logger.warn('Retrying webhook subscribe');     // console + Sentry breadcrumb
 *   logger.error('Payment failed', err);           // console + Sentry event
 *
 * Sentry is bootstrapped by ``initSentry`` (called from ``index.js`` at
 * boot). It stays completely inert until ``REACT_APP_SENTRY_DSN`` is set,
 * so local/preview builds without a DSN behave exactly like before —
 * no network calls, no bundle penalty at runtime.
 *
 * The ESLint rule `no-console` is configured to allow only `warn` and
 * `error` on raw `console`; use `logger.log/info/debug` for everything
 * else so we have a single choke-point.
 */
import * as Sentry from '@sentry/react';

const isDev =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production';

const noop = () => {};

// Track whether Sentry has actually been initialized. ``captureException``
// is safe to call before init (it's a no-op), but we guard breadcrumbs so
// we don't waste cycles building payloads that will get discarded.
let sentryReady = false;

/**
 * Initialize Sentry once at boot. Idempotent — safe to call twice.
 * Called from ``src/index.js`` before ``ReactDOM.render``.
 *
 * Configuration is driven entirely by ``REACT_APP_SENTRY_DSN``. When it's
 * empty, unset, or the placeholder ``"disabled"``, we bail out and the
 * whole logger reverts to console-only behaviour. This lets us ship the
 * Sentry code path to every environment (preview, prod) without requiring
 * a DSN — the user can flip it on later by setting the env var and
 * rebuilding.
 */
export function initSentry() {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
  if (!dsn || dsn === 'disabled' || sentryReady) return;

  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENV || (isDev ? 'development' : 'production'),
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      // Only forward warn+error automatically — logger.log/info/debug never
      // reach Sentry. Sampling is aggressive because most of our warns come
      // from webhook retries we already visualize elsewhere.
      tracesSampleRate: 0.0,          // no perf traces yet — flip on when needed
      replaysSessionSampleRate: 0.0,  // no session replays by default (privacy)
      replaysOnErrorSampleRate: 0.0,
      // Don't ship the request body, tokens, or cookies to Sentry — even
      // hashed. Better safe than PII-leaking.
      sendDefaultPii: false,
      beforeSend(event) {
        // Strip anything that looks like a bearer token / API key from the
        // error message and breadcrumb chain, just in case.
        if (event?.message) {
          event.message = event.message
            .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/g, 'Bearer [redacted]')
            .replace(/(api[_-]?key|token)["'\s:=]+[A-Za-z0-9._~+/=-]{16,}/gi, '$1=[redacted]');
        }
        return event;
      },
    });
    sentryReady = true;
  } catch (err) {
    // Swallow init failures — a broken DSN must never break the app.
    if (isDev) console.warn('[logger] Sentry init failed:', err);
  }
}

/**
 * Send a level-aware breadcrumb to Sentry so the surrounding events are
 * visible in the Sentry dashboard alongside the captured error.
 */
function breadcrumb(level, args) {
  if (!sentryReady) return;
  try {
    Sentry.addBreadcrumb({
      level,
      category: 'logger',
      message: args
        .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
        .join(' '),
    });
  } catch (_) { /* never let breadcrumb serialization crash callers */ }
}

function safeStringify(v) {
  try { return JSON.stringify(v); } catch (_) { return String(v); }
}

function captureError(args) {
  if (!sentryReady) return;
  try {
    // If any arg is an Error instance capture it directly so we get the
    // full stack. Otherwise capture a message so we still see the log.
    const err = args.find((a) => a instanceof Error);
    if (err) {
      Sentry.captureException(err, {
        extra: {
          args: args.map((a) => (a instanceof Error ? undefined : safeStringify(a))),
        },
      });
    } else {
      Sentry.captureMessage(
        args.map((a) => (typeof a === 'string' ? a : safeStringify(a))).join(' '),
        'error',
      );
    }
  } catch (_) { /* ignore */ }
}

export const logger = {
  // Dev-only channels — never go anywhere in production.
  log: isDev ? console.log.bind(console) : noop,
  debug: isDev ? console.debug.bind(console) : noop,
  info: isDev ? console.info.bind(console) : noop,

  // Warnings: always visible in devtools, breadcrumbed to Sentry.
  warn: (...args) => {
    console.warn(...args);
    breadcrumb('warning', args);
  },

  // Errors: always visible in devtools AND captured as a Sentry event.
  error: (...args) => {
    console.error(...args);
    breadcrumb('error', args);
    captureError(args);
  },
};

export default logger;
